/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/

import { authentication, EventEmitter, LogOutputChannel, Memento, Uri, workspace } from 'vscode';
import { Repository as GitHubRepository, RepositoryRuleset } from '@octokit/graphql-schema';
import { $flc, $ilc } from './auth';
import { API, BranchProtection, BranchProtectionProvider, BranchProtectionRule, Repository } from './typings/git';
import { $jlc, $klc } from './util';

const REPOSITORY_QUERY = `
	query repositoryPermissions($owner: String!, $repo: String!) {
		repository(owner: $owner, name: $repo) {
			defaultBranchRef {
				name
			},
			viewerPermission
		}
	}
`;

const REPOSITORY_RULESETS_QUERY = `
	query repositoryRulesets($owner: String!, $repo: String!, $cursor: String, $limit: Int = 100) {
		repository(owner: $owner, name: $repo) {
			rulesets(includeParents: true, first: $limit, after: $cursor) {
				nodes {
					name
					enforcement
					rules(type: PULL_REQUEST) {
						totalCount
					}
					conditions {
						refName {
							include
							exclude
						}
					}
					target
				},
				pageInfo {
					endCursor,
					hasNextPage
				}
			}
		}
	}
`;

export class $nlc {

	private readonly a = new $jlc();
	private readonly b = new $jlc();

	private c = false;
	private set d(enabled: boolean) {
		if (this.c === enabled) {
			return;
		}

		if (enabled) {
			for (const repository of this.f.repositories) {
				this.b.add(this.f.registerBranchProtectionProvider(repository.rootUri, new $olc(repository, this.g, this.h)));
			}
		} else {
			this.b.dispose();
		}

		this.c = enabled;
	}

	constructor(
		private readonly f: API,
		private readonly g: Memento,
		private readonly h: LogOutputChannel) {
		this.a.add(this.f.onDidOpenRepository(repository => {
			if (this.c) {
				this.b.add(f.registerBranchProtectionProvider(repository.rootUri, new $olc(repository, this.g, this.h)));
			}
		}));

		this.a.add(workspace.onDidChangeConfiguration(e => {
			if (e.affectsConfiguration('github.branchProtection')) {
				this.i();
			}
		}));

		this.i();
	}

	private i(): void {
		const config = workspace.getConfiguration('github', null);
		this.d = config.get<boolean>('branchProtection', true) === true;
	}

	dispose(): void {
		this.d = false;
		this.a.dispose();
	}

}

export class $olc implements BranchProtectionProvider {
	private readonly a = new EventEmitter<Uri>();
	onDidChangeBranchProtection = this.a.event;

	private b: BranchProtection[];
	private readonly c = `branchProtection:${this.d.rootUri.toString()}`;

	constructor(
		private readonly d: Repository,
		private readonly f: Memento,
		private readonly g: LogOutputChannel) {
		// Restore branch protection from global state
		this.b = this.f.get<BranchProtection[]>(this.c, []);

		d.status().then(() => {
			authentication.onDidChangeSessions(e => {
				if (e.provider.id === 'github') {
					this.j();
				}
			});
			this.j();
		});
	}

	provideBranchProtection(): BranchProtection[] {
		return this.b;
	}

	private async h(owner: string, repo: string): Promise<GitHubRepository> {
		const graphql = await $ilc();
		const { repository } = await graphql<{ repository: GitHubRepository }>(REPOSITORY_QUERY, { owner, repo });

		return repository;
	}

	private async i(owner: string, repo: string): Promise<RepositoryRuleset[]> {
		const rulesets: RepositoryRuleset[] = [];

		let cursor: string | undefined = undefined;
		const graphql = await $ilc();

		while (true) {
			const { repository } = await graphql<{ repository: GitHubRepository }>(REPOSITORY_RULESETS_QUERY, { owner, repo, cursor });

			rulesets.push(...(repository.rulesets?.nodes ?? [])
				// Active branch ruleset that contains the pull request required rule
				.filter(node => node && node.target === 'BRANCH' && node.enforcement === 'ACTIVE' && (node.rules?.totalCount ?? 0) > 0) as RepositoryRuleset[]);

			if (repository.rulesets?.pageInfo.hasNextPage) {
				cursor = repository.rulesets.pageInfo.endCursor as string | undefined;
			} else {
				break;
			}
		}

		return rulesets;
	}

	private async j(): Promise<void> {
		const branchProtection: BranchProtection[] = [];

		try {
			for (const remote of this.d.state.remotes) {
				const repository = $klc(remote.pushUrl ?? remote.fetchUrl ?? '');

				if (!repository) {
					continue;
				}

				// Repository details
				this.g.trace(`Fetching repository details for "${repository.owner}/${repository.repo}".`);
				const repositoryDetails = await this.h(repository.owner, repository.repo);

				// Check repository write permission
				if (repositoryDetails.viewerPermission !== 'ADMIN' && repositoryDetails.viewerPermission !== 'MAINTAIN' && repositoryDetails.viewerPermission !== 'WRITE') {
					this.g.trace(`Skipping branch protection for "${repository.owner}/${repository.repo}" due to missing repository write permission.`);
					continue;
				}

				// Get repository rulesets
				const branchProtectionRules: BranchProtectionRule[] = [];
				const repositoryRulesets = await this.i(repository.owner, repository.repo);

				for (const ruleset of repositoryRulesets) {
					branchProtectionRules.push({
						include: (ruleset.conditions.refName?.include ?? []).map(r => this.k(repositoryDetails, r)),
						exclude: (ruleset.conditions.refName?.exclude ?? []).map(r => this.k(repositoryDetails, r))
					});
				}

				branchProtection.push({ remote: remote.name, rules: branchProtectionRules });
			}

			this.b = branchProtection;
			this.a.fire(this.d.rootUri);

			// Save branch protection to global state
			await this.f.update(this.c, branchProtection);
			this.g.trace(`Branch protection for "${this.d.rootUri.toString()}": ${JSON.stringify(branchProtection)}.`);
		} catch (err) {
			this.g.warn(`Failed to update repository branch protection: ${err.message}`);

			if (err instanceof $flc) {
				// A GitHub authentication session could be missing if the user has not yet
				// signed in with their GitHub account or they have signed out. In this case
				// we have to clear the branch protection information.
				this.b = branchProtection;
				this.a.fire(this.d.rootUri);

				await this.f.update(this.c, undefined);
			}
		}
	}

	private k(repository: GitHubRepository, refName: string): string {
		if (refName.startsWith('refs/heads/')) {
			return refName.substring(11);
		}

		switch (refName) {
			case '~ALL':
				return '**/*';
			case '~DEFAULT_BRANCH':
				return repository.defaultBranchRef!.name;
			default:
				return refName;
		}
	}
}
